import {cadType, objectType} from "_src/constants";
import wasmHelper from "_src/client/manipulators/wasm_helper";
import {GeometryBuffer} from "_src/engine/geometry";
import {bufferType} from "_src/engine/constants";
import measurements from "_src/client/measurements";
import {createProperty} from "_src/reformer/property"
import {propertyType} from "_src/constants";
import appState from "_src/app_state";
import workspace from "_src/workspace";
import windowManager from "_src/client/window_manager";
import output from "_src/engine/output";
import {mat4, vec3, vec4} from "_src/libs/gl-matrix";
import {screenUtils} from "_src/client/utils";
import selector from "_src/manipulators/libs/selector";
import events from "_src/engine/events";
import intersectEvaluator from "_src/engine/intersect_evaluator";
import CadPicker from "_src/client/manipulators/cadpicker";

export default class Measurements extends CadPicker {

  constructor(camera) {
    super(camera);
    this._currentMeasure = {
      draggingIdx: -1,
      data: [],
    }
  }

  clear() {
    this._currentMeasure.data.length = 0;

    this._updateWidgets();
  }

  changeMeasurementType(type) {
    const oldType = appState.getStateValue("measurementType");
    if (type === oldType) return;

    this.clear();
    if (oldType === objectType.LINEAR_MEASURE) this._hideCadElements();
    if (type === objectType.LINEAR_MEASURE) this._showCadElements();

    appState.setStateValue("measurementType", type);
  }

  save() {
    const payload = {
      type: null,
      subtype: null,
      data: null,
    };

    if (this._currentMeasure) {
      payload.type = objectType.LINEAR_MEASURE;
      payload.data = this._currentMeasure.data;
      events.emit("saveMeasure", payload);
    }

    this.clear();
  }

  _mouseDownHandler({screenX, screenY}) {
    switch (appState.getStateValue("measurementType")) {
      case objectType.LINEAR_MEASURE:
        this._pickMeasurePoint(screenX, screenY);
        break;
    }
  }

  _mouseMoveHandler({screenX, screenY}) {
    switch (appState.getStateValue("measurementType")) {
      case objectType.LINEAR_MEASURE:
        super._mouseMoveHandler({screenX, screenY});

        if (this._currentMeasure.draggingIdx >= 0) {
          this._moveMeasurePoint(screenX, screenY);
        }
        break;
    }
  }

  _mouseUpHandler({screenX, screenY}) {
    switch (appState.getStateValue("measurementType")) {
      case objectType.LINEAR_MEASURE:
        if (this._currentMeasure.draggingIdx >= 0) {
          this._currentMeasure.draggingIdx = -1;
        } else {
          this._addMeasurePoint(screenX, screenY);
        }
        break;
    }
  }

  async _addMeasurePoint(screenX, screenY) {
    switch (appState.getStateValue("measurementType")) {
      case objectType.LINEAR_MEASURE:
        await this._addLinearMeasurePoint(screenX, screenY);
        break;
      case objectType.GEODESIC_MEASURE:
        this._addGeodesicMeasurePoint(screenX, screenY);
        break;
    }
  }

  async _addLinearMeasurePoint(screenX, screenY) {
    let {res, sphereCenter} = await this._pickHandler(screenX, screenY);
    if (!res) return;

    let isPerpendicular = false;
    if (res.object.type === objectType.CAD) {
      if (res.object.subtype === cadType.PLANE) {
        const prevPoint = this._currentMeasure.data[this._currentMeasure.data.length - 1];
        if (prevPoint && prevPoint["object-uuid"] !== res.object.getUUID()) {
          const intersectPoint = vec3.create();
          const planeTransform = mat4.mul(mat4.create(), res.object.model.modelMatrix, res.object.getModelData()["transform"]);
          const normal = vec4.transformMat4(vec4.create(), vec4.fromValues(0, 0, 1, 0), planeTransform);
          vec4.normalize(normal, normal);
          const planePoint = [planeTransform[12], planeTransform[13], planeTransform[14]];
          const point = prevPoint.position;
          const success = intersectEvaluator.linePlaneTest(normal, planePoint, point, normal, intersectPoint);
          if (success) {
            sphereCenter = intersectPoint;
            isPerpendicular = true;
          }
        }
      } else {
        let cadCorrectedPosition = res.object.pickPoint(screenX, screenY);
        if (cadCorrectedPosition) sphereCenter = cadCorrectedPosition;
      }
    }

    this._currentMeasure.data.push({
      "position": Array.from(sphereCenter),
      "object-uuid": res.object.getUUID(),
      "perpendicular": isPerpendicular,
    });

    this._updateWidgets();
  }

  _addGeodesicMeasurePoint(screenX, screenY) {
    output.error(output.DEVELOPERS_ERROR, `${screenX} ${screenY} geodesic measure creation is unsupported yet`);
  }

  _pickMeasurePoint(screenX, screenY) {
    const widget = measurements.getTemporalMeasure(objectType.LINEAR_MEASURE);
    if (!widget) return;

    const pointModels = widget.getPointModels();

    const mainWindow = windowManager.getWindow(windowManager.MAIN);
    const camera = mainWindow.renderGraph.getWorkspaceScene().camera;
    const {x, y} = screenUtils.toScreen(mainWindow.getViewport(), camera.viewport, screenX, screenY);

    const intersectedObject = selector.findClosestIntersection(null, x, y, camera, pointModels);
    this._currentMeasure.draggingIdx = pointModels.indexOf(intersectedObject);
  }

  async _moveMeasurePoint(screenX, screenY) {
    const {res, sphereCenter} = await this._pickHandler(screenX, screenY);
    if (!res) return;

    this._currentMeasure.data[this._currentMeasure.draggingIdx] = {
      "position": Array.from(sphereCenter),
      "object-uuid": res.object.getUUID(),
      "perpendicular": false,
    };

    this._updateWidgets();
  }

  async previewDM(srcObject, dstObject, distance) {
    const savedNormalsState = appState.getState(appState.GS_NORMALS);
    appState.setState(appState.GS_NORMALS, true);
    await workspace.applyState(srcObject);
    appState.setState(appState.GS_NORMALS, savedNormalsState);

    const {dm, dmInfo} = await wasmHelper.bakeDM(srcObject, dstObject, distance);
    const buffers = [];
    for (let i=0; i<dm.length; i++) {
      const chunkDM = dm[i];
      const distanceItem = new GeometryBuffer({buffer: chunkDM.buffer, type: bufferType.F32_1});
      buffers.push(distanceItem);
    }

    const distanceMapProperty = createProperty(propertyType.MEASURE);
    distanceMapProperty.setValue("searchDistance", distance);
    const dmWrapper = workspace._createDistanceMapWrapper(distanceMapProperty);
    measurements.addDistanceMapToObject(srcObject, buffers, dmWrapper);
    return dmInfo;
  }

  async _pickHandler(screenX, screenY) {
    const mainWindow = windowManager.getWindow(windowManager.MAIN);
    const camera = mainWindow.renderGraph.getWorkspaceScene().camera;

    const sphereCenter = vec3.create();
    const res = await mainWindow.getPixelWorldPoint(screenX, screenY, camera, sphereCenter);
    return {res, sphereCenter};
  }

  _updateWidgets() {
    this._updateMeasureWidgets();
  }

  _updateMeasureWidgets() {
    measurements.removeTemporalMeasure(objectType.LINEAR_MEASURE);
    if (this._currentMeasure.data.length === 0) return;

    const measureProperty = createProperty(propertyType.MEASURE);
    measureProperty.setValue("type", objectType.LINEAR_MEASURE);
    const measureWrapper = workspace._createDistanceMeasureWrapper(measureProperty);
    switch (appState.getStateValue("measurementType")) {
      case objectType.LINEAR_MEASURE: {
        measureProperty.setValue("geometries", this._currentMeasure.data);
        break;
      }
    }

    measurements.addTemporalMeasure(objectType.LINEAR_MEASURE, measureWrapper);
  }

  _onStart() {
    const type = appState.getStateValue("measurementType");
    if (type === objectType.LINEAR_MEASURE || type === objectType.GEODESIC_MEASURE) this._showCadElements();
    //wasmHelper.init();
  }

  _onStop() {
    const type = appState.getStateValue("measurementType");
    if (type === objectType.LINEAR_MEASURE || type === objectType.GEODESIC_MEASURE) this._hideCadElements();
    //wasmHelper.cleanup();
    this.clear();
  }

}
